/**
 * Pure logic for the custom color setup fee.
 *
 * Turns a /cart.js payload into a plan of fee-line mutations. No DOM, no
 * fetch, no timers — every rule here is testable under node --test.
 *
 * It owns: what counts as a custom-color line, when two typed colors are the
 * same color, and which fee lines the cart should hold.
 * It does NOT own: reading the cart, issuing mutations, or re-rendering. See
 * custom-color-reconciler.js.
 */
(function (root, factory) {
  'use strict';
  const isNode = typeof module !== 'undefined' && module.exports;
  const constants = isNode
    ? require('./custom-color-constants.js')
    : root.CustomColorConstants;
  const api = factory(constants);
  if (isNode) {
    module.exports = api;
  } else {
    root.CustomColorModel = api;
  }
})(typeof globalThis !== 'undefined' ? globalThis : this, function (C) {
  'use strict';

  /**
   * Fold a typed color into a comparison key.
   *
   * Trim, collapse internal whitespace, case-fold. Deliberately NOT stripping
   * punctuation and NOT reordering words: the accepted failure is charging
   * twice for one color written two ways, never waiving a fee for two
   * genuinely different colors. A double charge is visible to the customer
   * and refundable in one click; the opposite error is silent and permanent.
   */
  function normalizeColor(text) {
    if (typeof text !== 'string') return '';
    return text.trim().replace(/\s+/g, ' ').toLowerCase();
  }

  /**
   * Does this selection value mean "custom color"?
   *
   * SUBSTRING match, not equality, and the reason is load-bearing. The real
   * captured value is "Custom Color - Add Note [$500.00]": OPC appends its own
   * price annotation to the option label, so the string CHANGES AT CUTOVER —
   * the moment the option is set to $0 the "[$500.00]" becomes "[$0.00]" or
   * disappears. An equality match would work perfectly right up to the cutover
   * and then silently stop matching at the exact moment the fee starts being
   * this theme's job. The merchant can also reword the "- Add Note" half in the
   * OPC admin without telling anyone.
   *
   * The false-positive risk is a sibling option value that merely contains the
   * words: the captured `Finish` value is "Color + $47 Per Door Sqft [$47.00]",
   * which contains "color" but not "custom color", so it does not match.
   */
  function isCustomSelection(value) {
    const folded = normalizeColor(value);
    return C.CUSTOM_SELECTION_VALUES.some(function (needle) {
      return folded.indexOf(needle) !== -1;
    });
  }

  /**
   * Does this property key read as "the field holding the typed color"?
   *
   * A PATTERN, not a fixed list, because OPC's option sets are not labelled
   * consistently and nothing enforces that they will be. Observed 2026-08-31,
   * two products on the same store:
   *
   *   Revere Raised Panel           -> "Custom Color Info"
   *   Liberty Cathedral Raised Panel-> "Custom Color Information"
   *
   * The second was not in COLOR_TEXT_KEYS, so that door's color was never
   * read, never counted as distinct, and no fee was charged — silently, since
   * the model fails closed. Standardising the labels in OPC is the real fix
   * and should still happen; this makes the code survive the next divergence
   * rather than leaking $500 until someone notices.
   *
   * Anchored at the start so it cannot match the selection key ("Color") or an
   * unrelated option that merely mentions color somewhere in its label.
   *
   * BOTH SPELLINGS, from CUSTOM_COLOR_KEY_PREFIXES. The repo standardised on
   * American spelling, but an option-set label is merchant-typed text in
   * another system and nothing makes it follow. CUSTOM_SELECTION_VALUES
   * already matches "custom colour", so dropping it here does not skip the
   * line — it detects the line as custom and then cannot read its color, which
   * routes it to unnamedCustomLines and charges nothing. Fails open, which is
   * the one direction this model must never fail.
   */
  function isColorTextKey(key) {
    const folded = normalizeColor(key);
    return C.CUSTOM_COLOR_KEY_PREFIXES.some(function (prefix) {
      return folded.indexOf(prefix) === 0;
    });
  }

  /**
   * The typed color text on a line's properties, or null.
   *
   * COLOR_TEXT_KEYS is consulted first and in order, so the known-good keys
   * stay authoritative and their precedence is fixed. Anything else matching
   * isColorTextKey is a fallback, sorted so that a line carrying two such
   * properties resolves the same way every time rather than depending on
   * property insertion order.
   */
  function colorTextOf(props) {
    const named = C.COLOR_TEXT_KEYS.map(function (key) {
      return props[key];
    }).find(function (value) {
      return typeof value === 'string' && value.trim() !== '';
    });
    if (named) return named;

    const fallback = Object.keys(props)
      .filter(function (key) {
        return (
          C.COLOR_TEXT_KEYS.indexOf(key) === -1
          && isColorTextKey(key)
          && typeof props[key] === 'string'
          && props[key].trim() !== ''
        );
      })
      .sort();

    return fallback.length ? props[fallback[0]] : null;
  }

  /**
   * The customer's typed color, trimmed but otherwise as written.
   *
   * Returns null when the selection says custom but no text key holds a value.
   * That combination cannot arise through the storefront — `Custom Color Info`
   * is a REQUIRED field in the OPC option set (confirmed by the merchant,
   * 2026-08-31) — so this branch exists only for a malformed or hand-built
   * cart. It fails closed on purpose: a line we cannot name a color for gets
   * no fee, rather than an unidentifiable fee line staff cannot reconcile.
   */
  function colorDisplayOf(line) {
    const props = (line && line.properties) || null;
    if (!props) return null;
    const selection = C.SELECTION_KEYS.map(function (key) {
      return props[key];
    }).find(function (value) {
      return typeof value === 'string' && isCustomSelection(value);
    });
    if (!selection) return null;

    const text = colorTextOf(props);
    return text ? text.trim() : null;
  }

  /**
   * Comparison key for a piece of color text.
   *
   * Strips whitespace from the normalized text. The key answers "is this the
   * same color", so SW 7005 typed today and SW7005 typed tomorrow are the same
   * color. normalizeColor preserves spaces (they are needed to match
   * CUSTOM_SELECTION_VALUES like 'custom color'), but colorKeyFromText strips
   * them — the one place both a door's typed color and a fee line's stored
   * color are folded to the same identity.
   */
  function colorKeyFromText(text) {
    return normalizeColor(text).replace(/\s+/g, '');
  }

  /** Comparison key for a line's custom color, or null if it has none. */
  function colorKeyOf(line) {
    const display = colorDisplayOf(line);
    return display ? colorKeyFromText(display) : null;
  }

  /** Distinct colors in the cart, first-seen order, first-seen display text. */
  function distinctColors(lines) {
    const seen = Object.create(null);
    const out = [];
    (lines || []).forEach(function (line) {
      const key = colorKeyOf(line);
      if (!key || seen[key]) return;
      seen[key] = true;
      out.push({ key: key, display: colorDisplayOf(line) });
    });
    return out;
  }

  /**
   * Lines that say "custom color" but carry no readable color text.
   *
   * This is the shape of a silent revenue leak: the line IS custom, the fee
   * SHOULD be charged, and the model correctly refuses to invent a color it
   * cannot name — so nothing happens and nobody finds out. It happened for
   * real on 2026-08-31, when an option set labelled the field "Custom Color
   * Information" and the code only knew "Custom Color Info".
   *
   * isColorTextKey now covers that family, so reaching this list means a set
   * has diverged further still. The reconciler logs it; that is the only
   * warning anyone gets before a $500 fee quietly goes uncharged.
   */
  function unnamedCustomLines(lines) {
    return (lines || []).filter(function (line) {
      const props = (line && line.properties) || null;
      if (!props) return false;
      const selection = C.SELECTION_KEYS.map(function (key) {
        return props[key];
      }).find(function (value) {
        return typeof value === 'string' && isCustomSelection(value);
      });
      return Boolean(selection) && !colorTextOf(props);
    });
  }

  /** Is this line the fee product? Variant id is the only identity we trust. */
  function isFeeLine(line, feeVariantId) {
    return Boolean(line && feeVariantId && line.variant_id === feeVariantId);
  }

  /** Fee lines present in the cart, with the color each one stands for. */
  function feeLinesOf(lines, feeVariantId) {
    return (lines || [])
      .filter(function (line) {
        return isFeeLine(line, feeVariantId);
      })
      .map(function (line) {
        const props = line.properties || {};
        return {
          key: line.key,
          colorKey: colorKeyFromText(props[C.FEE_COLOR_PROPERTY] || ''),
          quantity: line.quantity,
        };
      });
  }

  /**
   * The mutations that would make the cart satisfy the invariant:
   * exactly one fee line per distinct custom color in the cart, and no others.
   *
   * `change` carries removals (quantity 0) and quantity corrections
   * (quantity 1) in one bucket — both are the same Ajax call downstream.
   *
   * Fails closed on an unconfigured fee variant: no variant, no plan. A
   * misconfigured theme setting must cost a missed fee, never a wrong mutation.
   */
  function planFor(lines, feeVariantId) {
    const empty = { add: [], change: [] };
    if (!feeVariantId) return empty;

    const wanted = distinctColors(lines);
    const present = feeLinesOf(lines, feeVariantId);

    const keep = Object.create(null);
    const change = [];

    present.forEach(function (fee) {
      const isWanted = wanted.some(function (c) {
        return c.key === fee.colorKey;
      });
      if (!isWanted || keep[fee.colorKey]) {
        change.push({ id: fee.key, quantity: 0 });
        return;
      }
      keep[fee.colorKey] = true;
      if (fee.quantity !== 1) change.push({ id: fee.key, quantity: 1 });
    });

    const add = wanted
      .filter(function (color) {
        return !keep[color.key];
      })
      .map(function (color) {
        const properties = {};
        properties[C.FEE_COLOR_PROPERTY] = color.display;
        return { id: feeVariantId, quantity: 1, properties: properties };
      });

    return { add: add, change: change };
  }

  return {
    normalizeColor,
    isCustomSelection,
    isColorTextKey,
    colorTextOf,
    colorKeyOf,
    colorKeyFromText,
    colorDisplayOf,
    distinctColors,
    unnamedCustomLines,
    isFeeLine,
    feeLinesOf,
    planFor,
  };
});
